// parts.jsx - Repeated blocks of the customer site. Anything that appears on more
// than one page lives here so the pages themselves stay readable as content.
//
// Everything that moves does so through ui/motion.jsx, and everything drawn comes
// from ui/illustrations.jsx. A page file should never contain a keyframe or a
// path element.
//
// LEGEND
//   1. Figure        a photograph when there is one, an illustration when not
//   2. PageHead      the standard page opening
//   3. TrustStrip    the four promises, as a hairline band
//   4. Steps         the numbered how-it-works list, with a drawn connector
//   5. Quote         a customer quote, set as an editorial pull quote
//   6. FaqList       accordion, also the source of the FAQ structured data
//   7. CtaBand       the closing call to action
//   8. OfferReceipt  the sample itemised offer used in the hero
//   9. StatRow       three or four numbers on a rule, counting up
//  10. BuyMarquee    the infinite strip of things people send
//  11. EmbedThis     the one line offer to take a tool away
//  12. MobileCta     the sticky bar that appears once the hero is passed
//  13. WhatHappensNext  the process, with a mark on where you are

import { useEffect, useState } from "react";
import { Link } from "react-router";
import clsx from "clsx";

import { money } from "../lib/format.js";
import { PHOTOS, srcSet } from "../lib/media.js";
import { Enter, Reveal, Stagger, StaggerBoxed, CountUp, Marquee, Tilt } from "../ui/motion.jsx";
import { MailerKit, Ornament } from "../ui/illustrations.jsx";
import { ArrowRight, ChevronDown, Shield, Video, Clock, Dollar } from "../ui/icons.jsx";

////////// 1. FIGURE //////////
// One component decides between a real photograph and the drawing. Pages ask
// for a slot by name and never have to know which they are getting, or hand it
// a path from KIT in lib/media.js when they want a particular photograph rather
// than whatever is currently in a slot.
//
// `sizes` is worth passing. The default assumes roughly half a wide screen,
// which is right for a figure beside a column of text and wrong for a small one
// in a grid, and getting it wrong costs a phone the difference between the 400
// and the 1200 wide file.

export function Figure({
  photo,
  src: explicit,
  fallback: Fallback,
  alt,
  className,
  imgClassName,
  sizes = "(min-width: 1024px) 46vw, 92vw",
  priority = false,
  halo = true,
}) {
  // A slot name, or a path straight out of KIT. PHOTOS lookup first, because a
  // slot that has been filled should win over a caller guessing a filename.
  const src = explicit ?? PHOTOS[photo];

  return (
    <figure className={clsx("relative", className)}>
      {halo && <span className="halo" aria-hidden="true" />}
      {src ? (
        <img
          src={src}
          srcSet={srcSet(src)}
          sizes={sizes}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          // Cut outs are objects, not crops. `object-contain` keeps a tall
          // display stand whole where `object-cover` would slice the top off it.
          className={clsx("w-full h-auto object-contain", imgClassName)}
        />
      ) : Fallback ? (
        <Fallback className={imgClassName} />
      ) : null}
    </figure>
  );
}

////////// 2. PAGE HEAD //////////

export function PageHead({ eyebrow, title, lede, children, className, art: ArtWork, wide = false }) {
  return (
    <header className={clsx("pt-10 sm:pt-14 md:pt-20 pb-8 md:pb-14 clip-x", className)}>
      <div className={clsx("shell", ArtWork && "grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:gap-14 lg:items-center")}>
        <div>
          {eyebrow && (
            <Enter as="p" from="none" className="eyebrow mb-3 sm:mb-4">{eyebrow}</Enter>
          )}
          <Enter as="h1" delay={60} className={clsx("display display-xl text-ink", wide ? "max-w-4xl" : "max-w-3xl")}>
            {title}
          </Enter>
          {lede && (
            <Enter as="p" delay={140} className="lede mt-5 md:mt-6 measure-wide">{lede}</Enter>
          )}
          {children && <Enter delay={220} className="mt-7">{children}</Enter>}
        </div>

        {ArtWork && (
          <Enter from="scale" delay={180} className="hidden lg:block">
            <ArtWork />
          </Enter>
        )}
      </div>
    </header>
  );
}

////////// 3. TRUST STRIP //////////
// Four claims we can back up, each one a sentence a customer would say out loud.
// No icons in circles, no cards, just rules.

// In the order the customer lives them, which also puts the camera last. It is
// the unusual promise rather than the important one, and leading with it answers
// a question nobody has asked yet.
export const PROMISES = [
  { icon: Shield, title: "Insured both directions", body: "Hand it to a staffed FedEx counter and keep the receipt, and it is covered from that scan until it reaches us, and on the way back." },
  { icon: Clock, title: "Offer in about a day", body: "Most packages are weighed, tested and quoted within 24 hours of arriving." },
  { icon: Dollar, title: "Paid the day you accept", body: "Accept before 3pm Eastern on a business day and the transfer goes out the same day, unless we still have to verify something." },
  { icon: Video, title: "Opened on camera", body: "Every package is recorded from seal to scale. You get the video on your tracking page." },
];

export function TrustStrip({ className }) {
  return (
    <section className={clsx("border-y border-rule bg-surface/40", className)}>
      <div className="shell">
        {/* .rule-grid owns the columns, the padding and the hairlines. Two
            columns with neither a gutter nor a rule between them put the second
            promise fourteen pixels from the end of the first. */}
        <StaggerBoxed as="ul" step={90} className="rule-grid">
          {PROMISES.map(({ icon: Glyph, title, body }) => (
            <li key={title} className="flex flex-col items-center justify-center text-center h-full">
              <Glyph size={19} className="text-bronze mb-3" />
              <p className="font-semibold text-[15px] text-ink">{title}</p>
              <p className="hint mt-1.5 leading-relaxed measure" style={{ marginInline: "auto" }}>{body}</p>
            </li>
          ))}
        </StaggerBoxed>
      </div>
    </section>
  );
}

////////// 4. STEPS //////////

export const STEPS = [
  {
    title: "Ask for a kit",
    text: "Tell us roughly what you have. A person reads every request, usually the same day, and posts a padded mailer with a prepaid insured FedEx label already inside.",
    aside: "Nothing to print. Nothing to pay.",
  },
  {
    title: "Send your items",
    text: "Put everything in, seal it, and hand it to any FedEx counter or drop box. You get a text the moment it scans, and again the moment it reaches our counter.",
    aside: "Insured from the second it is scanned.",
  },
  {
    title: "See the offer, decide",
    text: "We weigh each piece, test its purity, and put the whole breakdown on your tracking page, with the video of us opening the package. You have 48 hours to answer, and we requote free after that. Accept and the money goes out. Decline and your metal comes back free.",
    aside: "No haggling. No pressure. No fee either way.",
  },
];

export function Steps({ steps = STEPS, className }) {
  return (
    // Three across only once there is room for three readable measures. At 768
    // it was three 170px ribbons, so between a phone and a laptop each step lies
    // down instead: the numeral to the left of its own paragraph.
    <StaggerBoxed
      as="ol"
      step={110}
      from="up"
      className={clsx("grid gap-px bg-rule border border-rule lg:grid-cols-3", className)}
      itemClassName="bg-paper h-full"
    >
      {steps.map((step, index) => (
        <li key={step.title} className="p-6 sm:p-7 lg:p-8 h-full flex flex-col sm:flex-row sm:gap-7 lg:flex-col lg:gap-0">
          <div className="flex items-center gap-4 mb-4 sm:mb-0 sm:shrink-0 sm:self-start lg:mb-5 lg:self-auto">
            <span className="numeral">{String(index + 1).padStart(2, "0")}</span>
            {index < steps.length - 1 && (
              <span className="hidden lg:block flex-1 h-px bg-gradient-to-r from-bronze-soft/70 to-transparent" aria-hidden="true" />
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <h3 className="display display-sm text-ink">{step.title}</h3>
            <p className="hint mt-3 leading-relaxed flex-1 measure">{step.text}</p>
            {step.aside && (
              <p className="mt-4 pt-4 border-t border-rule-soft text-[13px] font-semibold text-bronze-dark">{step.aside}</p>
            )}
          </div>
        </li>
      ))}
    </StaggerBoxed>
  );
}

////////// 5. QUOTE //////////

export function Quote({ text, name, place, className, dark = false }) {
  return (
    <figure className={clsx("border-l-2 border-bronze-soft pl-4 sm:pl-5", className)}>
      {/* measure-wide so a quote stacked full width on a tablet still breaks at
          a readable line length rather than running the whole way across. */}
      <blockquote className={clsx("display display-sm leading-snug measure-wide", dark ? "text-paper" : "text-ink")}>
        {text}
      </blockquote>
      <figcaption className={clsx("mt-3.5 text-[13px]", dark ? "text-paper/45" : "hint")}>
        <span className={clsx("font-semibold", dark ? "text-paper/85" : "text-ink-soft")}>{name}</span>
        {place && <span> &middot; {place}</span>}
      </figcaption>
    </figure>
  );
}

////////// 6. FAQ //////////
// The answer panel animates its height with a grid trick, which is the only way
// to transition to an unknown height without measuring it in JavaScript.

export function FaqList({ items, className, defaultOpen = -1 }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={clsx("border-t border-rule", className)}>
      {items.map((item, index) => {
        const expanded = open === index;
        return (
          <div key={item.question} className="border-b border-rule">
            <h3>
              <button
                type="button"
                onClick={() => setOpen(expanded ? -1 : index)}
                aria-expanded={expanded}
                className="w-full flex items-start justify-between gap-5 py-4 sm:py-5 text-left group"
              >
                <span className={clsx("display display-sm transition-colors", expanded ? "text-ink" : "text-ink-soft group-hover:text-ink")}>
                  {item.question}
                </span>
                <ChevronDown
                  size={20}
                  className={clsx("shrink-0 mt-1 transition-transform duration-300", expanded ? "rotate-180 text-bronze" : "text-faint")}
                />
              </button>
            </h3>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p className="-mt-1 measure-wide text-[15px] sm:text-[15.5px] leading-relaxed text-muted whitespace-pre-line">
                  {item.answer}
                </p>
                {/* An optional next step for the small number of answers that
                    have one. Kept out of `answer` so the FAQ structured data
                    stays plain text. */}
                {item.link && (
                  <Link to={item.link.to} className="link text-[14.5px] text-ink font-semibold inline-block mt-2.5">
                    {item.link.label}
                  </Link>
                )}
                <div className="pb-5" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

////////// 7. CTA BAND //////////

export function CtaBand({
  title = "Find out what it is actually worth.",
  body = "The kit is free, the shipping is insured both ways, and there is nothing to sign until you have seen the offer.",
  primary = { to: "/get-a-kit", label: "Get a free kit" },
  secondary = { to: "/how-it-works", label: "See how it works" },
  className,
}) {
  return (
    <section className={clsx("bg-ink text-paper relative clip-x", className)}>
      {/* A slow bronze wash, off centre, so the band is not a flat rectangle. */}
      <span
        aria-hidden="true"
        className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none float-slow"
        style={{ background: "radial-gradient(closest-side, rgb(198 161 91 / 0.16), transparent 70%)" }}
      />
      <div className="shell band relative">
        <div className="grid gap-7 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <Reveal>
            <h2 className="display display-lg max-w-2xl">{title}</h2>
            <p className="mt-4 sm:mt-5 text-paper/65 measure-wide leading-relaxed">{body}</p>
          </Reveal>
          <Reveal delay={120} className="flex flex-wrap gap-3 lg:justify-end">
            <Link to={primary.to} className="btn btn-primary btn-lg w-full xs:w-auto">
              {primary.label} <ArrowRight size={17} />
            </Link>
            {secondary && (
              <Link to={secondary.to} className="btn btn-lg w-full xs:w-auto border border-paper/25 text-paper hover:bg-paper hover:text-ink">
                {secondary.label}
              </Link>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

////////// 8. SAMPLE OFFER //////////
// A real looking breakdown, marked clearly as an example. It shows the product in
// one glance far better than a stock photograph of jewellery would.

const SAMPLE_LOTS = [
  { label: "Two rope chains", detail: "14K, 21.4 g gross, 1.1 g stone", amount: 968 },
  { label: "Class ring", detail: "10K, 9.8 g gross, 1.4 g stone", amount: 246 },
  { label: "Sterling flatware, 34 pieces", detail: ".925, 612 g gross", amount: 331 },
];

export function OfferReceipt({ className, animate = true }) {
  const total = SAMPLE_LOTS.reduce((sum, lot) => sum + lot.amount, 0);

  return (
    <Tilt className={clsx("panel shadow-lift", className)} max={4}>
      <div className="flex items-center justify-between gap-4 px-4 sm:px-5 py-3.5 border-b border-rule">
        <div>
          <p className="eyebrow">Example offer</p>
          <p className="text-[13px] text-muted mt-0.5 num">Order IGS-7K3M2Q</p>
        </div>
        <span className="tag tag-bronze">Itemised</span>
      </div>

      <Stagger as="ul" step={110} start={220} className="divide-y divide-rule-soft">
        {SAMPLE_LOTS.map((lot) => (
          <li key={lot.label} className="flex items-baseline justify-between gap-4 px-4 sm:px-5 py-3.5">
            <span className="min-w-0">
              <span className="block text-[14.5px] text-ink font-medium truncate">{lot.label}</span>
              <span className="block text-[12.5px] text-faint mt-0.5 num">{lot.detail}</span>
            </span>
            <span className="num font-semibold text-ink shrink-0">{money(lot.amount)}</span>
          </li>
        ))}
      </Stagger>

      <div className="flex items-baseline justify-between gap-4 px-4 sm:px-5 py-4 bg-surface border-t border-rule">
        <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-muted">Total offer</span>
        <span className="display display-md text-ink">
          {animate
            ? <CountUp to={total} duration={1400} format={(value) => money(Math.round(value))} />
            : money(total)}
        </span>
      </div>

      <p className="fineprint px-4 sm:px-5 py-3 text-[11.5px] leading-relaxed text-faint border-t border-rule-soft">
        Illustration only. Your offer depends on the weight, purity and metal of what you actually send,
        and on the metal price the day we quote you.
      </p>
    </Tilt>
  );
}

////////// 9. STAT ROW //////////

export function StatRow({ items, className }) {
  return (
    <StaggerBoxed
      as="dl"
      step={90}
      from="scale"
      className={clsx("grid gap-px bg-rule border-y border-rule sm:grid-cols-3", className)}
      itemClassName="bg-paper h-full"
    >
      {/* Centred columns once there are columns. On a phone that became three
          tall blocks for three numbers, so each one lies down instead: label and
          hint on the left, the number on the right, the way a receipt reads. */}
      {items.map((item) => (
        <div
          key={item.label}
          className="grid grid-cols-[1fr_auto] items-center gap-x-5 py-4 px-4 text-left sm:block sm:py-7 sm:px-3 sm:text-center"
        >
          <dt className="eyebrow">{item.label}</dt>
          <dd className="display display-md text-ink row-span-2 sm:row-auto sm:mt-2">
            {typeof item.count === "number"
              ? <CountUp to={item.count} format={item.format} decimals={item.decimals || 0} prefix={item.prefix} suffix={item.suffix} />
              : <span className="num">{item.value}</span>}
          </dd>
          {item.hint && <dd className="hint mt-0.5 sm:mt-1">{item.hint}</dd>}
        </div>
      ))}
    </StaggerBoxed>
  );
}

////////// 10. BUY MARQUEE //////////
// The strip of things people actually post us. It is decorative, so it is hidden
// from assistive technology and there is a plain list of the same categories on
// the what we buy page.

const MARQUEE_ITEMS = [
  "Broken chains", "Class rings", "Single earrings", "Sterling flatware", "Dental gold",
  "Wedding bands", "Pocket watch cases", "Charm bracelets", "Pre-1965 coins", "Platinum settings",
  "Bench sweeps", "Silver bars", "Tangled costume lots", "Cufflinks", "Lockets",
];

export function BuyMarquee({ className }) {
  return (
    <div className={clsx("border-y border-rule bg-surface/30 py-3.5", className)}>
      <Marquee
        items={MARQUEE_ITEMS.map((item, index) => (
          <span key={item} className="inline-flex items-center gap-3 sm:gap-4">
            <span className="text-[13.5px] sm:text-[14.5px] text-ink-soft whitespace-nowrap">{item}</span>
            <span className={clsx("w-1 h-1 rounded-full", index % 3 === 0 ? "bg-bronze" : "bg-rule")} />
          </span>
        ))}
        itemClassName="px-3 sm:px-4"
        speed={54}
      />
    </div>
  );
}

////////// 11. EMBED THIS //////////
// A quiet offer on every tool page: take this one and put it on your own site.
// Deliberately one line rather than a panel. Somebody who came here to weigh a
// chain is not looking for an embed, and the small number of people who are will
// find it. The link opens the builder already set to the tool they were using.
export function EmbedThis({ tool, metal, className }) {
  return (
    <p className={clsx("hint", className)}>
      Want this on your own site?{" "}
      <Link to={`/embed?tool=${tool}${metal ? `&metal=${metal}` : ""}`} className="link text-ink font-semibold">
        Embed it free
      </Link>
      . One line of HTML, no account, and you can turn our name off.
    </p>
  );
}

////////// 12. MOBILE CTA //////////
// Slides up once the hero has scrolled past, on phones only. It is the single
// most valuable thing on a small screen and it costs no layout space.

export function MobileCta({ label = "Get a free kit", to = "/get-a-kit", after = 620 }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY > document.body.offsetHeight - 220;
      setShown(window.scrollY > after && !atBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [after]);

  return (
    <div className="sticky-cta" data-shown={shown ? "true" : "false"}>
      <Link to={to} className="btn btn-primary w-full">
        {label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

////////// 13. WHAT HAPPENS NEXT //////////
// The four beats of the process and roughly when each one lands. Read twice by
// the same person: on the confirmation page after they ask, and again in the
// dialog they get if they fill the form in a second time. One copy, because two
// accounts of our own turnaround that disagree is worse than either of them.
//
// `stage` marks how far along somebody already is. It comes from the order,
// so a customer whose kit shipped last week is not told the mailer is coming.

const NEXT_STEPS = [
  { stage: "intake", when: "Today", what: "A person reads your request. Everybody gets a human, usually within one business day." },
  { stage: "outbound", when: "Two to three days", what: "The mailer arrives. The prepaid insured label is already inside it." },
  { stage: "inbound", when: "When you are ready", what: "Put your items in, seal it, hand it to a person at a FedEx counter and keep the receipt." },
  { stage: "evaluation", when: "A day after it lands", what: "We open it on camera, weigh and test every piece, and your itemised offer appears on your tracking page." },
];

export function WhatHappensNext({ stage, className }) {
  // Anything past evaluation is past this list entirely, so nothing is marked
  // as still to come.
  const reached = NEXT_STEPS.findIndex((step) => step.stage === stage);
  const at = stage && reached === -1 ? NEXT_STEPS.length : reached;

  return (
    <ol className={clsx("border-l border-rule pl-6 space-y-6", className)}>
      {NEXT_STEPS.map(({ stage: key, when, what }, index) => {
        const done = at > index;
        const now = at === index;
        return (
          <li key={key} className="relative">
            <span className={clsx(
              "absolute -left-[27px] top-1.5 w-2 h-2 rounded-full ring-4 ring-paper",
              done ? "bg-bronze" : now ? "bg-bronze-dark" : "bg-bronze-soft"
            )} />
            <p className={clsx(
              "text-[13px] font-semibold uppercase tracking-[0.1em]",
              done ? "text-faint" : "text-bronze-dark"
            )}>
              {when}{done && " \u00b7 done"}
            </p>
            <p className={clsx("mt-1 leading-relaxed", done ? "text-faint text-[13px]" : "hint")}>{what}</p>
          </li>
        );
      })}
    </ol>
  );
}

////////// SHARED ART //////////
// Re-exported so a page imports its illustrations from the same place as its
// blocks, rather than reaching into ui/ directly.

export { MailerKit as KitFigure, Ornament };
